import { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { RoleRow } from '../../core/api/types';

export interface RoleForm { code: string; name: string; description: string; copyFromRoleId: string }

interface Props {
  open: boolean;
  editing: boolean;
  initial: RoleForm;
  roles: RoleRow[];
  saving: boolean;
  error: string;
  onClose: () => void;
  onSubmit: (form: RoleForm) => void;
}

const toCode = (name: string) => name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '');
/** Built-in role codes; the API refuses them too. */
export const RESERVED_ROLE_CODES = ['PLATFORM_ADMIN', 'ADMIN', 'SUPERADMIN'];

export function RoleDialog({ open, editing, initial, roles, saving, error, onClose, onSubmit }: Props) {
  const { t } = useTranslation();
  const [form, setForm] = useState(initial);
  const [codeTouched, setCodeTouched] = useState(false);
  useEffect(() => {
    if (!open) return;
    // A new business role starts from the business ADMIN, the ceiling every tenant role lives under.
    const admin = editing || initial.copyFromRoleId ? undefined : roles.find((r) => r.code === 'ADMIN' && r.organizationId);
    setForm(admin ? { ...initial, copyFromRoleId: admin.id } : initial);
    setCodeTouched(editing);
  }, [open, initial, editing, roles]);

  const reserved = RESERVED_ROLE_CODES.includes(form.code.trim().toUpperCase());
  const valid = form.code.trim() && form.name.trim() && !reserved;
  return (
    <Dialog open={open} onClose={() => !saving && onClose()} fullWidth maxWidth="xs">
      <DialogTitle>{editing ? t('permissions.editRole', { defaultValue: 'Edit role' }) : t('permissions.newRole', { defaultValue: 'New role' })}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2} mt={1}>
          <TextField
            autoFocus
            label={t('permissions.roleName', { defaultValue: 'Name' })}
            value={form.name}
            placeholder={t('permissions.roleNameHint', { defaultValue: 'e.g. Supervisor' })}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value, code: codeTouched ? f.code : toCode(e.target.value) }))}
          />
          <TextField
            label={t('permissions.roleCode', { defaultValue: 'Code' })}
            value={form.code}
            error={reserved}
            helperText={reserved ? t('permissions.reservedCode', { defaultValue: 'PLATFORM_ADMIN and ADMIN are built-in roles.' }) : undefined}
            inputProps={{ style: { fontFamily: 'ui-monospace, monospace' } }}
            onChange={(e) => { setCodeTouched(true); setForm((f) => ({ ...f, code: e.target.value.toUpperCase() })); }}
          />
          <TextField
            label={t('permissions.roleDescription', { defaultValue: 'Description' })}
            value={form.description}
            placeholder={t('permissions.roleDescriptionHint', { defaultValue: 'What this role is for' })}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
          {!editing && (
            <TextField
              select
              label={t('permissions.startFrom', { defaultValue: 'Start from' })}
              value={form.copyFromRoleId}
              helperText={t('permissions.startFromHint', { defaultValue: 'Copies that role’s permissions. You can change them afterwards.' })}
              onChange={(e) => setForm((f) => ({ ...f, copyFromRoleId: e.target.value }))}
            >
              <MenuItem value="">{t('permissions.emptyRole', { defaultValue: 'Empty role (no access)' })}</MenuItem>
              {roles.map((r) => <MenuItem key={r.id} value={r.id}>{t('permissions.copyOf', { defaultValue: 'Copy of {{name}}', name: r.name })}</MenuItem>)}
            </TextField>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={saving} onClick={onClose}>{t('permissions.cancel', { defaultValue: 'Cancel' })}</Button>
        <Button variant="contained" disabled={saving || !valid} onClick={() => onSubmit(form)}>
          {editing ? t('permissions.saveRole', { defaultValue: 'Save role' }) : t('permissions.createRole', { defaultValue: 'Create role' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
